import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { BarChart3, Building2, ListChecks, LogOut, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { BrandHeader } from "@/components/layouts/BrandHeader";

/**
 * Backoffice shell — sidebar on desktop, top header on mobile.
 * All paths under /app/* (URL space is shared across personas; the persona
 * decides which sidebar items are rendered).
 */
export function PartnerShell() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const { user, logout } = useAuth();

  const nav = [
    { to: "/app", label: "Overview", icon: BarChart3, exact: true },
    { to: "/app/profile", label: "Profile", icon: Building2, exact: false },
    { to: "/app/offerings", label: "Offerings", icon: ListChecks, exact: false },
    { to: "/app/orders", label: "Orders", icon: Receipt, exact: false },
  ] as const;

  return (
    <div className="flex min-h-screen bg-canvas">
      <aside className="hidden w-60 shrink-0 flex-col border-r bg-background md:flex">
        <BrandHeader compact />
        <nav className="flex-1 space-y-1 p-3 text-sm">
          {nav.map((item) => {
            const active = item.exact ? path === item.to : path.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to as never}
                className={cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 transition-colors",
                  active
                    ? "bg-secondary text-secondary-foreground"
                    : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                )}
              >
                <item.icon className="size-4" /> {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="border-t p-3">
          <div className="mb-2 truncate text-xs text-muted-foreground">{user?.email}</div>
          <Button
            onClick={() => void logout()}
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2"
          >
            <LogOut className="size-4" /> Sign out
          </Button>
        </div>
      </aside>

      <div className="flex flex-1 flex-col">
        <BrandHeader compact className="md:hidden">
          <span className="text-xs text-[#e3c071]/80">Partner</span>
        </BrandHeader>
        <main className="flex-1 p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
